import type i18next from "i18next";
import { api } from "@jourfx/convex/client";
import type { ConvexReactClient } from "convex/react";

export type SystemBrandingPublic = {
  enabled: boolean;
  colors: {
    primaryHex: string;
    secondaryHex?: string;
    accentHex?: string;
  };
  typography: {
    fontFamily: "lato";
    scale?: "standard" | "large";
  };
  texts: {
    de: Record<string, string>;
    en: Record<string, string>;
  };
  updatedAt: number;
};

const STORAGE_KEY = "jourfx.systemBranding.v1";
const OVERRIDE_NAMESPACES = ["common", "portalAccess", "techcheck", "video"] as const;
const SAGE_SCALE_KEYS = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900"] as const;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function hexToRgb(hex: string) {
  const m = hex.trim().match(/^#?([0-9a-fA-F]{6})$/);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return { r, g, b };
}

function rgbToHsl({ r, g, b }: { r: number; g: number; b: number }) {
  const r1 = r / 255;
  const g1 = g / 255;
  const b1 = b / 255;
  const max = Math.max(r1, g1, b1);
  const min = Math.min(r1, g1, b1);
  const d = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r1:
        h = ((g1 - b1) / d) % 6;
        break;
      case g1:
        h = (b1 - r1) / d + 2;
        break;
      default:
        h = (r1 - g1) / d + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function buildSageScaleFromPrimary(primaryHex: string): Record<string, string> | null {
  const rgb = hexToRgb(primaryHex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb);
  const baseS = clamp(s, 18, 70);
  const baseL = clamp(l, 18, 60);

  // Keep hue/sat mostly stable; vary lightness across the scale.
  const levels: Record<string, number> = {
    "50": baseL + 62,
    "100": baseL + 52,
    "200": baseL + 38,
    "300": baseL + 24,
    "400": baseL + 12,
    "500": baseL + 4,
    "600": baseL,
    "700": baseL - 6,
    "800": baseL - 12,
    "900": baseL - 18,
  };

  const out: Record<string, string> = {};
  for (const k of SAGE_SCALE_KEYS) {
    const lk = clamp(levels[k], 8, 98);
    out[k] = `hsl(${h} ${baseS}% ${lk}%)`;
  }
  return out;
}

function setCssVar(name: string, value: string | null) {
  if (value === null) {
    document.documentElement.style.removeProperty(name);
  } else {
    document.documentElement.style.setProperty(name, value);
  }
}

function applyTextOverrides(i18n: typeof i18next, locale: "de" | "en", entries: Record<string, string>) {
  const byNs = new Map<string, Record<string, any>>();

  for (const [fullKey, rawValue] of Object.entries(entries)) {
    const value = rawValue.trim();
    if (!value) continue;
    const [ns, ...rest] = fullKey.split(".");
    if (!ns || rest.length === 0) continue;

    const keyPath = rest;
    if (!byNs.has(ns)) byNs.set(ns, {});
    const root = byNs.get(ns)!;

    // Create nested object so keySeparator "." works naturally.
    let cur: any = root;
    for (let i = 0; i < keyPath.length; i++) {
      const part = keyPath[i]!;
      if (i === keyPath.length - 1) {
        cur[part] = value;
      } else {
        cur[part] = cur[part] ?? {};
        cur = cur[part];
      }
    }
  }

  for (const [ns, obj] of byNs.entries()) {
    i18n.addResourceBundle(locale, ns, obj, true, true);
  }
}

async function clearTextOverrides(i18n: typeof i18next) {
  for (const lng of ["de", "en"] as const) {
    for (const ns of OVERRIDE_NAMESPACES) {
      i18n.removeResourceBundle(lng, ns);
    }
  }
  // Reload defaults from backend; don't await per-namespace to keep it simple.
  await i18n.reloadResources(["de", "en"], [...OVERRIDE_NAMESPACES]);
}

export function readCachedBranding(): SystemBrandingPublic | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SystemBrandingPublic;
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.updatedAt !== "number") return null;
    if (!parsed.colors?.primaryHex) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeCachedBranding(branding: SystemBrandingPublic) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(branding));
  } catch {
    // ignore
  }
}

export function applyBrandingFromCache(i18n: typeof i18next) {
  const cached = readCachedBranding();
  if (cached) {
    void applyBranding(cached, i18n);
  }
}

export async function refreshBranding(convex: ConvexReactClient, i18n: typeof i18next) {
  const remote = (await convex.query((api as any).admin.branding.getPublic, {})) as SystemBrandingPublic;
  const cached = readCachedBranding();
  if (!cached || cached.updatedAt !== remote.updatedAt) {
    writeCachedBranding(remote);
    await applyBranding(remote, i18n);
  }
}

export async function applyBranding(branding: SystemBrandingPublic, i18n: typeof i18next) {
  // Make it easy to cache-bust images in UI code if needed.
  (window as any).__jourfxBrandingUpdatedAt = branding.updatedAt;

  if (!branding.enabled) {
    // Clear CSS overrides (fall back to default theme in CSS).
    for (const k of SAGE_SCALE_KEYS) setCssVar(`--sage-${k}`, null);
    setCssVar("--client-focus-ring", null);
    document.documentElement.style.removeProperty("--font-sans");
    document.documentElement.style.removeProperty("font-size");
    document.documentElement.dataset.branding = "default";

    // Restore default favicon.
    updateFavicon("/vite.svg");

    // Clear text overrides best-effort (reload defaults).
    try {
      await clearTextOverrides(i18n);
    } catch {
      // ignore
    }
    return;
  }

  const sageScale = buildSageScaleFromPrimary(branding.colors.primaryHex);
  if (sageScale) {
    for (const k of SAGE_SCALE_KEYS) setCssVar(`--sage-${k}`, sageScale[k] ?? null);
    // Focus ring follows the mid scale.
    setCssVar("--client-focus-ring", sageScale["400"]);
  }

  // Typography (phase 1: only Lato, but keep the hook for future fonts)
  if (branding.typography?.fontFamily === "lato") {
    setCssVar("--font-sans", "\"Lato\", system-ui, sans-serif");
  }

  // Scale (Versicherte often benefit from larger text)
  if (branding.typography?.scale === "large") {
    document.documentElement.style.fontSize = "20px";
  } else {
    document.documentElement.style.removeProperty("font-size");
  }

  applyTextOverrides(i18n, "de", branding.texts?.de ?? {});
  applyTextOverrides(i18n, "en", branding.texts?.en ?? {});

  // Dynamic favicon from branding endpoint.
  const convexSiteUrl = (
    import.meta.env.VITE_CONVEX_SITE_URL ||
    import.meta.env.VITE_CONVEX_URL ||
    "http://localhost:3211"
  ).replace(/\/$/, "");
  updateFavicon(`${convexSiteUrl}/api/branding/favicon?v=${branding.updatedAt}`);

  document.documentElement.dataset.branding = "custom";
}

function updateFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}
